import React from 'react';
import { Box, Typography } from '@mui/material';
import { Comment as CommentIcon } from '@mui/icons-material';
import { SurveyResponseForAudit } from '../types';
import { ResponseInclusionToggle } from './ResponseInclusionToggle';

interface ResponseInclusionControlsProps {
  response: SurveyResponseForAudit;
  question: any; // Question definition with options
  onInclusionChange: (params: {
    responseId: string;
    responseIncluded: boolean;
    commentIncluded: boolean;
  }) => void;
  disabled?: boolean;
  showTooltips?: boolean;
}

export const ResponseInclusionControls: React.FC<ResponseInclusionControlsProps> = ({
  response,
  question,
  onInclusionChange,
  disabled = false,
  showTooltips: _showTooltips = true,
}) => {
  // Hook to format response values, mapping option IDs to labels
  const formatResponseValue = (value: string | number | string[]): string => {
    const getOptionLabel = (optionId: string | number): string => {
      if (!question?.options) return String(optionId);

      const option = question.options.find((opt: any) => opt.id === String(optionId));
      if (option?.label) {
        // Handle multilingual labels
        if (typeof option.label === 'object') {
          return (
            option.label.en ||
            option.label.no ||
            option.label.de ||
            Object.values(option.label)[0] ||
            String(optionId)
          );
        }
        return option.label;
      }
      return String(optionId);
    };

    if (Array.isArray(value)) {
      return value.map((v) => getOptionLabel(v)).join(', ');
    }

    // Handle Yes/No questions (boolean values)
    if (
      question?.type?.toLowerCase().includes('yes') ||
      question?.type?.toLowerCase().includes('boolean') ||
      typeof value === 'boolean' ||
      value === 'true' ||
      value === 'false'
    ) {
      if (value === 'true') return 'Yes';
      if (value === 'false') return 'No';
    }

    // For any question with options, try to map the ID to label
    // This covers single choice, multiple choice, scale, etc.
    if (question?.options && question.options.length > 0) {
      return getOptionLabel(value);
    }

    return String(value);
  };

  return (
    <Box
      sx={{
        p: 1.5,
        mb: 1,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        backgroundColor: 'background.paper',
        '&:hover': {
          borderColor: 'primary.main',
        },
      }}
    >
      {/* Compact Response Display */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ flex: 1, mr: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: response.isIncluded ? 600 : 400 }}>
            {formatResponseValue(response.responseValue)}
          </Typography>
          {response.comment && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <CommentIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {response.comment}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Inclusion Toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ResponseInclusionToggle
            responseId={response.id}
            questionId={question.questionId}
            isResponseIncluded={response.isIncluded}
            isCommentIncluded={response.isCommentIncluded}
            hasComment={!!response.comment}
            onInclusionChange={onInclusionChange}
            disabled={disabled}
          />
        </Box>
      </Box>
    </Box>
  );
};
